import { cn } from "@devhop/ui";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  DOCUMENT_CURRENCIES,
  type DocumentCurrency,
  emptyCurrencyBuckets,
  formatDocumentMoney,
} from "@/shared/lib/performance-document-currency";
import {
  formatDateLocal,
  formatDateTimeLocal,
} from "@/shared/lib/date-time-performance";
import { CompactMoneyAmount } from "@/shared/ui/CompactMoneyAmount";
import type { CurrencyAmountBuckets } from "../../domain/contracts/reports";

const NAV_ITEMS = [
  { label: "ພາບລວມ", to: "/app/performance/reports" },
  { label: "ງານສະແດງ", to: "/app/performance/reports/events" },
  { label: "ຄຳຂໍລູກຄ້າ", to: "/app/performance/reports/requests" },
  { label: "ນັກສະແດງ", to: "/app/performance/reports/performers" },
  { label: "ຈ່າຍນັກສະແດງ", to: "/app/performance/reports/payouts" },
  { label: "ລາຍຮັບ", to: "/app/performance/reports/revenue" },
] as const;

export function PerformanceReportsNav() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <nav className="mb-6 flex flex-wrap gap-1 border-b pb-0">
      {NAV_ITEMS.map((item) => {
        const active = pathname === item.to;
        return (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              "-mb-px border-b-2 px-4 py-2 text-sm transition-colors",
              active
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

export function formatPerformanceMoney(
  value: number,
  currency: DocumentCurrency = "LAK",
) {
  return formatDocumentMoney(value, currency);
}

function renderCurrencyBucketAmount(
  amount: number,
  currency: DocumentCurrency,
) {
  if (currency === "LAK") {
    return (
      <CompactMoneyAmount
        amount={amount}
        currencyClassName="font-medium text-base sm:text-lg"
      />
    );
  }
  return formatDocumentMoney(amount, currency);
}

/** Stacked lines by default; use `layout: "inline"` on summary KPI cards. */
export function formatCurrencyBuckets(
  buckets: CurrencyAmountBuckets | null | undefined,
  options?: {
    showEmpty?: boolean;
    className?: string;
    layout?: "stack" | "inline";
  },
) {
  const data = buckets ?? emptyCurrencyBuckets();
  const entries = DOCUMENT_CURRENCIES.filter(
    (c) => options?.showEmpty || data[c] !== 0,
  );
  const list = entries.length > 0 ? entries : (["LAK"] as const);
  const layout = options?.layout ?? "stack";

  if (layout === "inline") {
    return (
      <div
        className={cn(
          "flex flex-wrap items-baseline gap-x-2 gap-y-1",
          options?.className,
        )}
      >
        {list.map((currency, index) => (
          <span
            key={currency}
            className="inline-flex items-baseline gap-2 tabular-nums"
          >
            {index > 0 ? (
              <span className="text-muted-foreground/50" aria-hidden>
                ·
              </span>
            ) : null}
            {renderCurrencyBucketAmount(data[currency], currency)}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("space-y-0.5", options?.className)}>
      {list.map((currency) => (
        <div key={currency} className="tabular-nums">
          {renderCurrencyBucketAmount(data[currency], currency)}
        </div>
      ))}
    </div>
  );
}

/** Options for multi-currency values inside report summary stat cards. */
export const summaryStatCurrencyBucketOptions = {
  showEmpty: true,
  layout: "inline" as const,
  className: "text-xl sm:text-2xl",
};

export function formatCurrencyBucketsInline(
  buckets: CurrencyAmountBuckets | null | undefined,
  options?: { showEmpty?: boolean },
): string {
  const data = buckets ?? emptyCurrencyBuckets();
  const entries = DOCUMENT_CURRENCIES.filter(
    (c) => options?.showEmpty || data[c] !== 0,
  );
  const list = entries.length > 0 ? entries : (["LAK"] as const);
  return list
    .map((currency) => formatDocumentMoney(data[currency], currency))
    .join(" · ");
}

export function formatPerformanceDate(value: Date | string | null | undefined) {
  if (!value) return "—";
  return formatDateLocal(value);
}

export function formatPerformanceDateTime(
  value: Date | string | null | undefined,
) {
  if (!value) return "—";
  return formatDateTimeLocal(value);
}

export function escapeCsvCell(value: string | number): string {
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
